import { createContext, useCallback, useContext, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import s from './Feedback.module.css';
import { Icon } from './Icon';
import { Button } from './Button';

export function Spinner({ size = 20, label }: { size?: number; label?: string }) {
  return <span className={s.spinner} style={{ width: size, height: size }} role="status" aria-label={label ?? 'Loading'} />;
}

export function PageLoader({ label = 'Loading…' }: { label?: string }) {
  return <div className={s.pageLoader}><div style={{ display: 'grid', justifyItems: 'center', gap: 12 }}><Spinner size={26} /><span>{label}</span></div></div>;
}

export function Skeleton({ width = '100%', height = 14, style, radius }: { width?: number | string; height?: number | string; style?: CSSProperties; radius?: number }) {
  return <div className={s.skeleton} style={{ width, height, borderRadius: radius, ...style }} aria-hidden />;
}

export function SkeletonRows({ rows = 5, height = 44 }: { rows?: number; height?: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 16 }} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => <Skeleton key={i} height={height} />)}
    </div>
  );
}

export function EmptyState({ icon = 'sparkle', title, children, actions, compact }: { icon?: string; title: ReactNode; children?: ReactNode; actions?: ReactNode; compact?: boolean }) {
  return (
    <div className={[s.empty, compact && s.compact].filter(Boolean).join(' ')}>
      <div className={s.emptyIcon}><Icon name={icon} size={24} /></div>
      <div className={s.emptyTitle}>{title}</div>
      {children ? <div className={s.emptyBody}>{children}</div> : null}
      {actions ? <div className={s.emptyActions}>{actions}</div> : null}
    </div>
  );
}

export function errorMessage(err: unknown): string {
  if (!err) return 'Something went wrong.';
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'Something went wrong.';
}

export function ErrorState({ error, onRetry, title = 'Couldn’t load this' }: { error: unknown; onRetry?: () => void; title?: string }) {
  return (
    <div className={s.empty} role="alert">
      <div className={s.emptyIcon} style={{ background: 'var(--bad-soft)', color: 'var(--bad)' }}><Icon name="alert" size={24} /></div>
      <div className={s.emptyTitle}>{title}</div>
      <div className={s.emptyBody}>{errorMessage(error)}</div>
      {onRetry ? <div className={s.emptyActions}><Button icon="refresh" onClick={onRetry}>Try again</Button></div> : null}
    </div>
  );
}

export function Callout({ tone = 'info', title, children, action, icon }: { tone?: 'info' | 'warn' | 'bad' | 'ok' | 'accent' | 'promo'; title?: ReactNode; children?: ReactNode; action?: ReactNode; icon?: string }) {
  const defaultIcon = tone === 'ok' ? 'check' : tone === 'info' ? 'info' : tone === 'promo' ? 'gift' : tone === 'accent' ? 'sparkle' : 'alert';
  return (
    <div className={[s.callout, s[tone]].join(' ')} role={tone === 'bad' ? 'alert' : undefined}>
      <span className={s.calloutIcon}><Icon name={icon ?? defaultIcon} size={18} /></span>
      <div className={s.calloutBody}>
        {title ? <div className={s.calloutTitle}>{title}</div> : null}
        {children}
      </div>
      {action ? <div className={s.calloutAction}>{action}</div> : null}
    </div>
  );
}

/** Render loading / error / data for a TanStack query in one line. */
export function QueryView<D>({ query, children, loading, empty, isEmpty }: {
  query: UseQueryResult<D>; children: (data: D) => ReactNode; loading?: ReactNode; empty?: ReactNode; isEmpty?: (d: D) => boolean;
}) {
  if (query.isPending) return <>{loading ?? <SkeletonRows />}</>;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (empty && isEmpty?.(query.data)) return <>{empty}</>;
  return <>{children(query.data)}</>;
}

// ── Toasts ─────────────────────────────────────────────────────────────────
type ToastTone = 'ok' | 'bad' | 'info';
interface ToastItem { id: number; tone: ToastTone; message: ReactNode }
interface ToastApi { ok: (m: ReactNode) => void; error: (m: ReactNode | unknown) => void; info: (m: ReactNode) => void }
const ToastCtx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const push = useCallback((tone: ToastTone, message: ReactNode) => {
    const id = ++seq.current;
    setItems((xs) => [...xs.slice(-3), { id, tone, message }]);
    setTimeout(() => dismiss(id), tone === 'bad' ? 7000 : 4000);
  }, [dismiss]);
  const api = useMemo<ToastApi>(() => ({
    ok: (m) => push('ok', m),
    info: (m) => push('info', m),
    error: (m) => push('bad', m instanceof Error || typeof m !== 'object' || m === null ? errorMessage(m) : (m as ReactNode)),
  }), [push]);
  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className={s.toastStack} aria-live="polite" aria-atomic="false">
        {items.map((t) => (
          <div key={t.id} className={['glass', s.toast, s[t.tone]].join(' ')} role={t.tone === 'bad' ? 'alert' : 'status'}>
            <span className={s.toastIcon}><Icon name={t.tone === 'ok' ? 'check' : t.tone === 'bad' ? 'alert' : 'info'} size={18} /></span>
            <span>{t.message}</span>
            <button className={s.toastClose} onClick={() => dismiss(t.id)} aria-label="Dismiss"><Icon name="x" size={15} /></button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast outside ToastProvider');
  return ctx;
}
